import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import {
  cardHover,
  fadeIn,
  glassCard,
  iconHover,
  pageTransition,
  slideUp,
  staggerContainer,
  textReveal,
} from '@/lib/animations';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  DollarSign,
  Mail,
  MessageSquare,
  Package,
  Phone,
  Smartphone,
} from 'lucide-react';

const AiConnect = () => {
  const communicationChannels = [
    {
      name: 'Voice Calls',
      icon: Phone,
      count: '12 today',
      status: 'active',
      color: 'from-blue-500 to-blue-600',
    },
    {
      name: 'Web Chat',
      icon: MessageSquare,
      count: '28 today',
      status: 'active',
      color: 'from-purple-500 to-purple-600',
    },
    {
      name: 'SMS/Text',
      icon: Smartphone,
      count: '45 today',
      status: 'active',
      color: 'from-green-500 to-green-600',
    },
    {
      name: 'Email',
      icon: Mail,
      count: '15 today',
      status: 'active',
      color: 'from-orange-500 to-orange-600',
    },
  ];

  const quickActions = [
    {
      name: 'Schedule Appointment',
      description: 'Book, reschedule or cancel appointments',
      icon: Calendar,
    },
    {
      name: 'Order Status',
      description: 'Check glasses or contact lens orders',
      icon: Package,
    },
    {
      name: 'Business Hours',
      description: 'View clinic hours and availability',
      icon: Clock,
    },
    {
      name: 'Insurance & Pricing',
      description: 'Get information about coverage and costs',
      icon: DollarSign,
    },
  ];

  const recentInteractions = [
    {
      name: 'John Smith',
      action: 'Scheduled eye exam for March 25',
      time: '10 minutes ago',
      status: 'completed',
      avatar: 'JS',
    },
    {
      name: 'Sarah Johnson',
      action: 'Checked contact lens order status',
      time: '25 minutes ago',
      status: 'completed',
      avatar: 'SJ',
    },
    {
      name: 'Michael Brown',
      action: 'Inquired about insurance coverage',
      time: '1 hour ago',
      status: 'completed',
      avatar: 'MB',
    },
  ];

  return (
    <Layout>
      <motion.div className="w-full space-y-6" {...pageTransition}>
        {/* Header */}
        <motion.div
          className="w-full"
          variants={fadeIn}
          initial="initial"
          animate="animate"
        >
          <motion.h1
            className="text-3xl font-bold text-slate-900 dark:text-white light:text-slate-900 mb-2"
            variants={textReveal}
          >
            Ai Connect
          </motion.h1>
          <motion.p
            className="text-slate-700 dark:text-slate-300 light:text-slate-600"
            variants={textReveal}
          >
            Ai-powered communication hub for seamless patient interactions
          </motion.p>
        </motion.div>

        {/* Communication Channels */}
        <motion.div
          className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {communicationChannels.map((channel) => (
            <motion.div key={channel.name} variants={glassCard}>
              <GlassCard>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <motion.div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-r ${channel.color} flex items-center justify-center`}
                      whileHover={iconHover}
                    >
                      <channel.icon className="w-6 h-6 text-white" />
                    </motion.div>
                    <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-600 dark:text-green-400 light:text-green-700">
                      {channel.status}
                    </span>
                  </div>
                  <motion.h3
                    className="text-lg font-semibold text-slate-900 dark:text-white light:text-slate-900 mb-1"
                    variants={textReveal}
                  >
                    {channel.name}
                  </motion.h3>
                  <motion.p
                    className="text-slate-600 dark:text-slate-400 light:text-slate-600 text-sm mb-3"
                    variants={textReveal}
                  >
                    AI-powered phone interactions
                  </motion.p>
                  <motion.p
                    className="text-sm text-slate-700 dark:text-slate-300 light:text-slate-700"
                    variants={textReveal}
                  >
                    {channel.count}
                  </motion.p>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </motion.div>

        {/* Quick Actions and Recent Interactions */}
        <motion.div
          className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {/* Quick Actions */}
          <motion.div variants={glassCard}>
            <GlassCard>
              <div className="p-4">
                <motion.h2
                  className="text-xl font-semibold text-slate-900 dark:text-white light:text-slate-900 mb-4"
                  variants={textReveal}
                >
                  Quick Actions
                </motion.h2>
                <div className="space-y-4">
                  {quickActions.map((action) => (
                    <motion.div
                      key={action.name}
                      variants={slideUp}
                      whileHover={cardHover}
                    >
                      <GlassCard className="!rounded-2xl">
                        <div className="flex items-center space-x-4 p-4 rounded-lg hover:bg-slate-100/50 dark:hover:bg-white/5 light:hover:bg-white/30 transition-colors duration-200 cursor-pointer">
                          <motion.div
                            className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center"
                            whileHover={iconHover}
                          >
                            <action.icon className="w-5 h-5 text-white" />
                          </motion.div>
                          <div>
                            <motion.h3
                              className="text-slate-900 dark:text-white light:text-slate-900 font-medium"
                              variants={textReveal}
                            >
                              {action.name}
                            </motion.h3>
                            <motion.p
                              className="text-slate-600 dark:text-slate-400 light:text-slate-600 text-sm"
                              variants={textReveal}
                            >
                              {action.description}
                            </motion.p>
                          </div>
                        </div>
                      </GlassCard>
                    </motion.div>
                  ))}
                </div>
              </div>
            </GlassCard>
          </motion.div>

          {/* Recent Interactions */}
          <motion.div variants={glassCard}>
            <GlassCard>
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <motion.h2
                    className="text-xl font-semibold text-slate-900 dark:text-white light:text-slate-900"
                    variants={textReveal}
                  >
                    Recent Interactions
                  </motion.h2>
                  <motion.div whileHover={iconHover}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 dark:text-blue-400 light:text-blue-600 hover:text-blue-700 dark:hover:text-blue-300 light:hover:text-blue-700 transition-colors duration-200"
                    >
                      View All
                    </Button>
                  </motion.div>
                </div>
                <motion.div
                  className="space-y-4"
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                >
                  {recentInteractions.map((interaction, index) => (
                    <motion.div
                      key={index}
                      variants={slideUp}
                      whileHover={cardHover}
                    >
                      <GlassCard className="!rounded-2xl">
                        <div className="p-3">
                          <div className="flex items-start space-x-3">
                            <motion.div
                              className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0"
                              whileHover={iconHover}
                            >
                              <span className="text-white text-xs font-medium">
                                {interaction.avatar}
                              </span>
                            </motion.div>
                            <div className="flex-1">
                              <motion.p
                                className="text-slate-900 dark:text-white light:text-slate-900 font-medium text-sm"
                                variants={textReveal}
                              >
                                {interaction.name}
                              </motion.p>
                              <motion.p
                                className="text-slate-600 dark:text-slate-400 light:text-slate-600 text-xs"
                                variants={textReveal}
                              >
                                {interaction.action}
                              </motion.p>
                            </div>
                            <div className="text-right">
                              <motion.p
                                className="text-slate-600 dark:text-slate-400 light:text-slate-600 text-xs"
                                variants={textReveal}
                              >
                                {interaction.time}
                              </motion.p>
                              <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-600 dark:text-green-400 light:text-green-700">
                                {interaction.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </GlassCard>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </GlassCard>
          </motion.div>
        </motion.div>
      </motion.div>
    </Layout>
  );
};

export default AiConnect;
